import { useEffect, useState } from 'react';
import { logger } from 'src/logger';

// Custom event for same-window localStorage changes
export const STORAGE_CHANGE_EVENT = 'localStorageChange';

/**
 * Dispatches a custom event when localStorage is changed in the same window.
 * The native `storage` event only fires in OTHER tabs, so same-tab listeners
 * rely on this custom event to react to writes.
 */
export function dispatchStorageChangeEvent(key: string, newValue: string | null): void {
  window.dispatchEvent(
    new CustomEvent(STORAGE_CHANGE_EVENT, {
      detail: { key, newValue }
    })
  );
}

/**
 * A custom React hook that persists state in localStorage.
 * Automatically syncs changes across components and browser tabs.
 *
 * @template T The type of the state value to be persisted
 * @param {string} localStorageKey The key under which to store the value in localStorage
 * @param {T} defaultValue The default value to use if no value exists in localStorage
 * @returns {[T, React.Dispatch<React.SetStateAction<T>>]} A tuple containing the current value and a setter function
 *
 * @example
 * const [count, setCount] = usePersistedState('myCounter', 0);
 * // count will persist across page reloads and sync across components/tabs
 * setCount(count + 1);
 */
export function usePersistedState<T>(localStorageKey: string, defaultValue: T): [T, React.Dispatch<React.SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => {
    try {
      const localStorageItem = localStorage.getItem(localStorageKey);
      return localStorageItem !== null ? JSON.parse(localStorageItem) : defaultValue;
    } catch (err: any) {
      logger.error(`Error parsing local storage item "${localStorageKey}":`, err);
      return defaultValue;
    }
  });

  // Update localStorage when value changes
  useEffect(() => {
    try {
      const serializedValue = JSON.stringify(value);
      localStorage.setItem(localStorageKey, serializedValue);
      // Dispatch custom event for same-window synchronization
      dispatchStorageChangeEvent(localStorageKey, serializedValue);
    } catch (err: any) {
      logger.error(`Error setting local storage item "${localStorageKey}":`, err);
    }
  }, [value, localStorageKey]);

  // Listen for localStorage changes from other components (same window) and other tabs
  useEffect(() => {
    // Handler for same-window changes (custom event)
    const handleCustomStorageChange = (e: Event): void => {
      const customEvent = e as CustomEvent<{ key: string; newValue: string | null }>;
      if (customEvent.detail.key === localStorageKey && customEvent.detail.newValue !== null) {
        try {
          // Only update if the serialized value actually changed to prevent infinite loops
          const currentSerialized = JSON.stringify(value);
          if (currentSerialized !== customEvent.detail.newValue) {
            const newValue = JSON.parse(customEvent.detail.newValue);
            setValue(newValue);
          }
        } catch (err: any) {
          logger.error(`Error parsing storage change for "${localStorageKey}":`, err);
        }
      }
    };

    // Handler for cross-tab changes (native storage event)
    const handleStorageChange = (e: StorageEvent): void => {
      if (e.key === localStorageKey && e.newValue !== null) {
        try {
          // Only update if the serialized value actually changed for consistency
          const currentSerialized = JSON.stringify(value);
          if (currentSerialized !== e.newValue) {
            const newValue = JSON.parse(e.newValue);
            setValue(newValue);
          }
        } catch (err: any) {
          logger.error(`Error parsing storage change for "${localStorageKey}":`, err);
        }
      }
    };

    window.addEventListener(STORAGE_CHANGE_EVENT, handleCustomStorageChange);
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener(STORAGE_CHANGE_EVENT, handleCustomStorageChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [localStorageKey, value]);

  return [value, setValue];
}
